import { PropertyInfoCollector } from "./PropertyInfoCollector";
import { PropertyData, ReadyData } from "./PropertyInfoCollectorTypes";
import "../css/propertyInfoStyle.css";
import {
   GlassFoldUdBox,
   GroupSelector,
   KeyValueLine,
   KeyValuesBoxGrid,
   KeyValuesHorizontal,
   ShowSelectedGroup,
   ValueGroup,
} from "./PropertyComponents";
import { PropInfoGroupFormatters } from "./PropertyInfoGroupFormatters";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { MitStorage } from "src/managers/MitStorage";
import { FavEditor } from "./FavEditor";
import { GlassButton } from "src/components/GlassButtons";
import { useContext, useState, useEffect, Fragment } from "react";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { useTingbogSectionElements } from "./TingbogPropertySections";
import { ServitutRedegoerelseLaunchButton } from "src/components/ServitutRedegoerelseRapport";

type Props = {
   callBackOnLoaded?: (data: PropertyData) => void;
   bfeNr?: string | number
   allowExtendedInformation: boolean;
   foldetUd?: boolean;
   cols?: number;
};

export function CondominiumView(props: Props) {
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const [collector] = useState(
      new PropertyInfoCollector({
         BFEnr: Number(props.bfeNr)
      })
   );

   function setSelectedGroup(string) {
      MitStorage.setValue("CondominiumSelectedTab", string)
      _setSelectedGroup(string)
   }

   const [selectedGroup, _setSelectedGroup] = useState(
      MitStorage.getValue("CondominiumSelectedTab") || "Ejerlejlighed"
   );
   const [availableFavorites, _setAvailableFavorites] = useState<{ [fav: string]: string[] }>(
      MitStorage.getValue("PropertyFavorites") as { [fav: string]: string[] } || { "minFavorit": [] }
   )

   function setAvailableFavorites(res) {
      MitStorage.setValue("PropertyFavorites", res)
      _setAvailableFavorites(res)
   }

   const [showFavEditor, setShowFavEditor] = useState(false);


   const [availableGroup, setAvailableGroup] = useState(hasAccessToFeature(Feature.PropertyInfoFavorite) ? ["Favoritter"] : []);
   const [data, setData] = useState<Partial<PropertyData>>({});
   const [loading, setLoading] = useState(true)
   const tingbogSections = useTingbogSectionElements({
      data,
      hasAccessToFeature,
      foldetUd: props.foldetUd || false,
      extraServitutActions: (displayData) => (
         <ServitutRedegoerelseLaunchButton
            propertyData={displayData}
            hasAccessToFeature={hasAccessToFeature}
            onMouseDown={(event) => {
               event.preventDefault();
               event.stopPropagation();
            }}
         />
      ),
   });


   useEffect(() => {
      getDataAsync();
   }, [hasAccessToFeature]);

   function getDataAsync() {
      function setOnCallback<T extends keyof PropertyData>(
         val: ReadyData<PropertyData[T]>,
         key: T
      ) {
         if (val.status == "Ready" && (val.data || key === "ViamapTingBogAll")) {
            setData((a) => {
               return { ...a, [key]: val.data ?? (key === "ViamapTingBogAll" ? [] : val.data) };
            });
         }
         if (val.status == "Error" && key === "ViamapTingBogAll") {
            setData((a) => {
               return { ...a, ViamapTingBogAll: [] };
            });
         }
      }
      if (props.allowExtendedInformation) {
         // collector.requestData("ViamapTingBog", setOnCallback);
      }
      collector.requestData("Vurd", setOnCallback);
      collector.requestData("BBRRel", setOnCallback);
      collector.requestData("BBRBFETeknik", setOnCallback);
      collector.requestData("ViamapHandelsOplysning", setOnCallback);
      collector.requestData("MatEjerlejlighed", (setOnCallback as any));
      collector.requestData("BBRBFEEnhed", (setOnCallback as any));
      collector.requestData("BBRBFEBygning", (setOnCallback as any));
      collector.requestData("ViamapEjerskab", (setOnCallback as any))
      collector.requestData("ViamapTingBog", (setOnCallback as any))
      collector.requestData("ViamapTingBogAll", (setOnCallback as any))
      collector.requestData("DARAdresserEjendom", setOnCallback);
      collector.callBackOnFinished((data: PropertyData) => {
         props.callBackOnLoaded?.(data);
         setLoading(false)
      });
   }

   if (Object.keys(data).length === 0) {
      return <>Loading... {props.bfeNr}</>;
   }

   function PrintHeader() {
      let printDate = new Date().toLocaleDateString();

      return (
         <div className="only-print print-header" style={{}}>
            <div style={{ display: "inline-block" }}>
               <h4>Ejerlejlighed</h4>
               <h5>Printed: {printDate}</h5>
            </div>
            {/* <div style={{float:"right", marginRight:"25px"}}> <div className={"mit-viamap-icon"} id={"mit-viamap-icon"}/></div> */}
         </div>
      );
   }

   return (
      <>
         <PrintHeader />

         {SettingsManager.getSystemSetting("debuggingShowExtraDebugging") ?
            <GlassButton onClick={() => console.info(data)} >Log to console</GlassButton>
            : null}

         {/* ----------- AREAL ------------ */}
         <KeyValuesHorizontal>
            {PropInfoGroupFormatters.EjerLejligheden(data["DARAdresserEjendom"], data["MatEjerlejlighed"])}
         </KeyValuesHorizontal>
         <GroupSelector
            selected={selectedGroup}
            group={availableGroup}
            onSelect={setSelectedGroup}
         />
         {selectedGroup == "Favoritter" ?
            <>
               <div style={{ marginLeft: "auto" }}>
                  <GlassButton onClick={() => setShowFavEditor(true)} ><MIBIcon icon="favorite" /> {Localization.getText("Edit")}</GlassButton>
               </div>
               <FavEditor show={showFavEditor} favBoxes={availableFavorites} setFavBoxes={setAvailableFavorites} onClose={() => setShowFavEditor(false)} />
            </>
            : null}
         <div className="Scroll-content">
            <ShowSelectedGroup
               keepSelected={true}
               loading={loading}
               columns={props.cols!}
               group={selectedGroup}
               groupCallBack={(groups) =>
                  setAvailableGroup([...new Set(["Ejerlejlighed", ...groups, "Vurderinger", "Alle"])])
               }
            >

               {data["MatEjerlejlighed"] ? (
                  <GlassFoldUdBox
                     title="Arealer"
                     boxKey="Arealer"
                     group="Ejerlejlighed"
                     foldetUd={props.foldetUd || false}
                  >
                     <KeyValuesBoxGrid minWidth="200px" maxCols={2}>
                        {PropInfoGroupFormatters.LejAreal(data["MatEjerlejlighed"])}
                     </KeyValuesBoxGrid>
                  </GlassFoldUdBox>
               ) : (
                  <></>
               )}

               {data["ViamapHandelsOplysning"] ? (
                  <GlassFoldUdBox
                     title="Handelsoplysninger"
                     boxKey="Handelsoplysninger"
                     group="Ejerlejlighed"
                     foldetUd={props.foldetUd || false}
                  >
                     {/* <KeyValuesBoxGrid minWidth="200px" maxCols={2}> */}
                     {PropInfoGroupFormatters.HandelsOplysninger(data)?.map(
                        (a, idx) => {
                           if (!a?.length) return <></>;
                           return (
                              <ValueGroup key={a?.[0]?.val + idx}>
                                 {a?.map?.((b) => (
                                    <Fragment key={b.key}>
                                       <KeyValueLine
                                          key={b.key}
                                          valKey={b.key}
                                          value={b.val}
                                       />
                                    </Fragment>
                                 ))}
                              </ValueGroup>
                           );
                        }
                     )}
                     {/* </KeyValuesBoxGrid> */}
                  </GlassFoldUdBox>
               ) : (
                  <></>
               )}

               {data["ViamapEjerskab"] ? (
                  <GlassFoldUdBox
                     title="Ejerforhold"
                     group="Ejerlejlighed"
                     boxKey="Ejerforhold"
                     foldetUd={props.foldetUd || false}
                  >
                     {PropInfoGroupFormatters.Ejerforhold(data)}
                  </GlassFoldUdBox>
               ) : (
                  <></>
               )}
               {data.BBRSFE ? (
                  <GlassFoldUdBox
                     title="Samlet Fast Ejendom"
                     group="Ejerlejlighed"
                     boxKey="Samlet Fast Ejendom"
                     foldetUd={props.foldetUd || false}
                  >
                     {PropInfoGroupFormatters.SFE(data)}
                  </GlassFoldUdBox>
               ) : (
                  <></>
               )}
               {"BBRBFEBygning" in data && (data.BBRBFEBygning as any)?.length ? (
                  <GlassFoldUdBox
                     title={"BBR Bygning"}
                     group="BBR"
                     foldetUd={props.foldetUd || false}
                  >
                     {PropInfoGroupFormatters.BBREvery(data.BBRBFEBygning)}
                  </GlassFoldUdBox>) : <></>}
               {"BBRBFEEnhed" in data && (data.BBRBFEEnhed as any)?.length ? (
                  <GlassFoldUdBox
                     title={"BBR Enhed"}
                     group="BBR"
                     foldetUd={props.foldetUd || false}
                  >
                     {PropInfoGroupFormatters.BBREvery(data.BBRBFEEnhed)}
                  </GlassFoldUdBox>) : <></>}
               {"BBRRel" in data && (data.BBRRel as any)?.length ? (
                  <GlassFoldUdBox
                     title={"BBR Relation"}
                     group="BBR"
                     foldetUd={props.foldetUd || false}
                  >
                     {PropInfoGroupFormatters.BBREvery(data.BBRRel)}
                  </GlassFoldUdBox>) : <></>}
               {"BBRBFETeknik" in data && (data.BBRBFETeknik as any)?.length ? (
                  <GlassFoldUdBox
                     title={"BBR Teknik"}
                     group="BBR"
                     foldetUd={props.foldetUd || false}
                  >
                     {PropInfoGroupFormatters.BBREvery(data.BBRBFETeknik)}
                  </GlassFoldUdBox>) : <></>}


               {tingbogSections}
               {PropInfoGroupFormatters.Vurderinger(data, props.foldetUd || false)}
            </ShowSelectedGroup>
         </div>
      </>
   );
}
